import { useEffect, useRef, useState } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { analyzeFile, analyzeYouTube, pollYtJob } from "../api/server";
import type { Analysis } from "../types/domain";
import { UploadIcon, LinkIcon, OfflineIcon, CloseIcon } from "./icons";
import { useUi } from "../state/ui";
import { useT } from "../i18n";
import { keyFromSearch, songPath, songPathWithKey, videoIdFromLocation } from "../core/songUrl";
import { bandRoute } from "../core/bandRoute";

type Phase = { kind: "idle" } | { kind: "analyzing"; label: string } | { kind: "error"; msg: string };

const MAX_FILE_MB = 100;   // keep in sync with server MAX_UPLOAD_MB + nginx client_max_body_size

export function SongInput() {
  const c = useControllers();
  const t = useT();
  const loadAnalysis = useProjectStore((s) => s.loadAnalysis);
  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);
  const inputOpen = useUi((s) => s.inputOpen);
  const setInputOpen = useUi((s) => s.setInputOpen);
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [url, setUrl] = useState("");
  const [drag, setDrag] = useState(false);

  const stopAll = () => { c.song.pause(); c.youtube.stop(); c.metro.pause(); };

  // the address bar follows the loaded song: /watch?v=<id> for a YouTube analysis, / otherwise
  // (core/songUrl). Opening such an address loads that video (cache hit = instant), and back/forward
  // moves between songs.
  const ytRef = useRef(handleYouTube);
  ytRef.current = handleYouTube;
  const openingRef = useRef<string | null>(null);   // video being opened from the address (no double load)
  useEffect(() => {
    const openFromLocation = () => {
      if (bandRoute(location.pathname)) return;
      const v = videoIdFromLocation(location.pathname, location.search);
      const cur = useProjectStore.getState().source;
      if (v) {
        if ((cur.kind === "youtube" && cur.videoId === v) || openingRef.current === v) return;
        const yurl = `https://www.youtube.com/watch?v=${v}`;
        setUrl(yurl);
        openingRef.current = v;
        ytRef.current(yurl).finally(() => { if (openingRef.current === v) openingRef.current = null; });
      } else if (cur.kind !== "default") {
        stopAll();
        useProjectStore.getState().reset();
      }
    };
    // first load: tidy the address (drop &list=…, map old /chords/<id> and /?v=) without a new entry,
    // keeping a shared link's key/capo (…&transpose=&capo=, applied by the key store)
    const v = videoIdFromLocation(location.pathname, location.search);
    if (v) {
      const key = keyFromSearch(location.search);
      const tidy = key ? songPathWithKey({ kind: "youtube", videoId: v }, key) : `/watch?v=${v}`;
      if (location.pathname + location.search !== tidy) history.replaceState(null, "", tidy);
    }
    openFromLocation();
    const unsub = useProjectStore.subscribe((s, prev) => {
      if (bandRoute(location.pathname)) return;
      if (s.source === prev.source) return;
      // same video already in the address (maybe with key/capo params) → leave it
      if (s.source.kind === "youtube" && videoIdFromLocation(location.pathname, location.search) === s.source.videoId) return;
      const path = songPath(s.source);
      if (location.pathname + location.search !== path) history.pushState(null, "", path);
    });
    window.addEventListener("popstate", openFromLocation);
    return () => { unsub(); window.removeEventListener("popstate", openFromLocation); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(file: File) {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setPhase({ kind: "error", msg: t("input.toobig") });
      return;
    }
    setPhase({ kind: "analyzing", label: t("input.analyzing") });
    try {
      const analysis = await analyzeFile(file);
      stopAll();
      c.song.load(URL.createObjectURL(file));
      loadAnalysis(analysis, { kind: "localFile", fileName: file.name }, file.name.replace(/\.[^.]+$/, ""));
      setPhase({ kind: "idle" });
      setInputOpen(false);
    } catch {
      setPhase({ kind: "error", msg: t("input.err") });
    }
  }

  async function handleYouTube(rawUrl?: string) {
    const u = (rawUrl ?? url).trim();
    if (!u) return;
    setPhase({ kind: "analyzing", label: t("input.analyzing_yt") });
    try {
      const res = await analyzeYouTube(u);
      let analysis: Analysis & { video_id: string };
      if ("queued" in res && res.queued) {
        setPhase({ kind: "analyzing", label: t("input.queued") });
        analysis = await pollYtJob(res.job_id, () => setPhase({ kind: "analyzing", label: t("input.analyzing_yt") }));
      } else {
        analysis = res as Analysis & { video_id: string };
      }
      stopAll();
      await c.youtube.load(analysis.video_id);
      loadAnalysis(analysis, { kind: "youtube", videoId: analysis.video_id }, "YouTube");
      setPhase({ kind: "idle" });
      setInputOpen(false);
    } catch {
      setPhase({ kind: "error", msg: t("input.err") });
    }
  }

  const online = typeof navigator === "undefined" || navigator.onLine;

  // a song is loaded → the card collapses into the song header's "다른 곡" button (keeps the score on
  // the first screen). Stay mounted while analyzing so the status line remains visible.
  if (hasChart && !inputOpen && phase.kind !== "analyzing") return null;

  return (
    <section className="section">
      <div className="section-head">
        <h2>{t("input.title")}</h2>
        {hasChart && (
          <button className="icon-btn" onClick={() => setInputOpen(false)} aria-label={t("input.close")} title={t("input.close")}>
            <CloseIcon size={18} />
          </button>
        )}
      </div>
      <div className="input-card">
        <div
          className={"drop" + (drag ? " over" : "")}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
        >
          <UploadIcon size={32} />
          <div className="drop-title">{t("input.drop")}</div>
          <div className="drop-sub">{t("input.formats")}</div>
          <input ref={fileRef} type="file" accept="audio/*,video/*" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.currentTarget.value = ""; }} />
        </div>

        <div className="or-divider"><span className="hr" /><span className="or">{t("input.or")}</span><span className="hr" /></div>

        <div className="url-row">
          <span className="url-ico"><LinkIcon size={16} /></span>
          <input className="url-input" type="text" inputMode="url" placeholder={t("input.yturl")}
            value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleYouTube(); }} />
          <button className="btn-pri" onClick={() => handleYouTube()} disabled={phase.kind === "analyzing"}>{t("input.analyze")}</button>
        </div>
        <div className="yt-hint">{t("input.ythint")}</div>

        {phase.kind === "analyzing" && (
          <div className="status analyzing"><span className="spinner" />{phase.label}</div>
        )}
        {phase.kind === "error" && <div className="status err">{phase.msg}</div>}
        {!online && (
          <div className="banner"><OfflineIcon size={16} /> {t("input.offline")}</div>
        )}
      </div>
    </section>
  );
}
